import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { OtpChallenge, OtpIdentity } from '@/lib/auth/otp';
import { formValue } from '@/lib/forms';

import { useClientDeps } from './deps';

/**
 * BP-01 §6 `client /login`: "phone → OTP placeholder (FakeMessenger prints code
 * in dev)", states "idle, code-sent, error".
 *
 * The transport is the placeholder seam (`lib/auth/otp.ts`). This form talks to
 * no api route, because §5 defines none for it — see that module for exactly what
 * BP-04 replaces.
 */

/** §6's three states, plus the terminal one the journey ends in. */
export type OtpStage = 'idle' | 'sending' | 'code-sent' | 'verifying' | 'signed-in';

/**
 * `onSignedIn` is BP-11 R9's return path.
 *
 * Optional, and the default is BP-01's behaviour — the panel saying who you
 * are. A caller that passes it is saying "I have somewhere to send them",
 * which is what a deep link tapped while signed out needs: §11's journey is
 * "open `/b/<ref>` → login → lands on the booking", and the last arrow is
 * this callback.
 */
export function PhoneOtpForm({
  onSignedIn,
  headingLevel = 'h1',
}: {
  onSignedIn?: (identity: OtpIdentity) => void;
  /** `h2` when the form sits under another page's own heading, as on the landing page. */
  headingLevel?: 'h1' | 'h2';
} = {}): JSX.Element {
  const Heading = headingLevel;
  const deps = useClientDeps();
  const { t } = useTranslation('auth');
  const [stage, setStage] = useState<OtpStage>('idle');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [identity, setIdentity] = useState<OtpIdentity | null>(null);
  const [error, setError] = useState<'phone' | 'otp' | null>(null);

  function sendCode(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const phone = formValue(new FormData(event.currentTarget), 'phone');
    setStage('sending');
    setError(null);
    void deps.otp
      .request(phone)
      .then((issued) => {
        setChallenge(issued);
        setStage('code-sent');
      })
      .catch(() => {
        setError('phone');
        setStage('idle');
      });
  }

  function verifyCode(event: FormEvent<HTMLFormElement>, pending: OtpChallenge): void {
    event.preventDefault();
    const code = formValue(new FormData(event.currentTarget), 'code');
    setStage('verifying');
    setError(null);
    void deps.otp
      .verify(pending, code)
      .then((who) => {
        setIdentity(who);
        setStage('signed-in');
        onSignedIn?.(who);
      })
      .catch(() => {
        setError('otp');
        setStage('code-sent');
      });
  }

  if (identity !== null && onSignedIn === undefined) {
    return (
      <section className="space-y-2" data-testid="otp-signed-in">
        <Heading className="text-lg font-semibold">{t('otp.title')}</Heading>
        <p role="status">{t('otp.signedIn', { phone: identity.phone })}</p>
        <p data-testid="otp-role">{t('otp.role')}</p>
      </section>
    );
  }

  return (
    <section className="space-y-4" data-testid="phone-otp-form">
      <Heading className="text-lg font-semibold">{t('otp.title')}</Heading>

      {error === null ? null : (
        <p role="alert" data-testid="otp-error" className="rounded bg-status-no-show p-3 text-destructive">
          {t(`error.${error}`)}
        </p>
      )}

      <form onSubmit={sendCode} className="space-y-2">
        <div>
          <label className="block">
            <span className="block text-sm text-muted-foreground">{t('otp.phone')}</span>
            <input
              name="phone"
              type="tel"
              autoComplete="tel"
              defaultValue="+91"
              className="mt-1 w-full rounded border border-border p-2"
            />
          </label>
          <span className="text-xs text-muted-foreground">{t('otp.phoneHint')}</span>
        </div>
        <button
          type="submit"
          disabled={stage === 'sending'}
          className="w-full rounded bg-primary p-2 text-primary-foreground disabled:opacity-60"
        >
          {stage === 'sending' ? t('otp.sending') : t('otp.send')}
        </button>
      </form>

      {challenge === null ? null : (
        <form
          onSubmit={(event) => {
            verifyCode(event, challenge);
          }}
          className="space-y-2"
          data-testid="otp-code-form"
        >
          <p role="status">{t('otp.sent', { phone: challenge.phone })}</p>
          {challenge.devCode === null ? null : (
            <p data-testid="otp-dev-code" className="font-mono text-sm">
              {t('otp.devCode', { code: challenge.devCode })}
            </p>
          )}
          <label className="block">
            <span className="block text-sm text-muted-foreground">{t('otp.code')}</span>
            <input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="mt-1 w-full rounded border border-border p-2"
            />
          </label>
          <button
            type="submit"
            disabled={stage === 'verifying'}
            className="w-full rounded bg-primary p-2 text-primary-foreground disabled:opacity-60"
          >
            {stage === 'verifying' ? t('otp.verifying') : t('otp.verify')}
          </button>
        </form>
      )}
    </section>
  );
}
